import { useMutation, useQuery } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import WarningAmber from "@mui/icons-material/WarningAmber";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CommandButton } from "components/CommandButton";
import { ConfirmDialog } from "components/ConfirmDialog";
import { TextCellWithTooltip } from "components/DataGridCell";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TierIIYearPicker } from "components/TierIIYearPicker";
import {
  CHEMICAL_EXCLUSION_HAZARDS_TOOLTIP,
  chemicalExclusionWarning,
} from "encamp-shared/src/tierii/chemicalExclusionWarning";
import { isChemicalReporting } from "encamp-shared/src/tierii/isChemicalReporting";
import {
  prettyPrintAmountCode,
  prettyPrintQuantity,
} from "encamp-shared/src/utils/prettyPrintUnits";
import { gql } from "generated-graphql";
import { FacilityChemicalsQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useProducts } from "hooks/useProducts";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useSelectedReportingYear } from "hooks/useSelectedReportingYear";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { formatChemicalName } from "util/chemicalName";

import { useFacility } from "../Facility/useFacility";
import { ChemicalsTableProps } from "./ChemicalsTable";
import { AddEditFacilityChemicalDialog } from "./Inventory/Facility/FacilityChemicalAddEditDialog";
import { GetReportDetailsQueryName, useIgnoredIssues } from "./Report/useReport";

export type FacilityChemicalRow = FacilityChemicalsQuery["facilityChemicals"]["items"][0];

const FACILITY_CHEMICALS_QUERY = gql(/* GraphQL */ `
  query FacilityChemicals(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    facilityChemicals(
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      items {
        id
        maxAmount
        maxAmountCode
        averageAmount
        averageAmountCode
        unit
        isAlwaysReported
        isExempt
        facility {
          state
        }
        chemical {
          id
          ...ChemicalPicker
          alternateId
          isEhs
          noHazardsNotReporting
          physicalHazards
          healthHazards
        }

        reportingFacilityChemical {
          id
          isOverThreshold
        }

        issues {
          ...issue
        }
      }
      count
    }
  }
`);

const FACILITY_INVENTORY_STATUS = gql(`
  query GetFacilityChemicalInventoryStatus($facilityId: ID!, $toYear: Int!) {
    facilityInventoryStatus(facilityId: $facilityId, toYear: $toYear, inventory: CHEMICAL) {
      exists
      mostRecentYear
    }
  }
`);

const DELETE_FACILITY_CHEMICAL = gql(/* GraphQL */ `
  mutation DeleteFacilityChemical($id: ID!) {
    deleteFacilityChemical(id: $id) {
      id
    }
  }
`);

const COPY_CHEMICAL_INVENTORY = gql(`
  mutation CopyChemicalInventory($facilityId: ID!, $toYear: Int!) {
    copyFacilityChemicals(facilityId: $facilityId, toYear: $toYear) {
      fromYear
    }
  }
`);

export function FacilityChemicalsTable({ facilityId, reportId }: ChemicalsTableProps) {
  const ignoredIssues = useIgnoredIssues();

  const alerts = useAlerts();
  const reportingYear = useSelectedReportingYear();
  const { hasProducts: isOtherChemicals } = useProducts();
  const [deletingChemical, setDeletingChemical] = useState<FacilityChemicalRow | undefined>();

  const { data: facilityQuery } = useFacility(facilityId);

  const {
    selectedId: facilityChemicalId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("facilityChemicalId");
  const [addingChemical, setAddingChemical] = useState(false);

  const [deleteFacilityChemical, { loading: isDeleting }] = useMutation(DELETE_FACILITY_CHEMICAL, {
    onCompleted() {
      alerts.success("Successfully removed the chemical");
      setDeletingChemical(undefined);
    },
    onError(err) {
      alerts.error("There was an error removing the chemical", err);
      setDeletingChemical(undefined);
    },
    refetchQueries: ["FacilityChemicals"].concat(
      reportId // Include report details if we are in a report context
        ? [GetReportDetailsQueryName, "ReportingFacilityChemicals"]
        : []
    ),
  });

  const [copyChemicalInventory, { loading: isCopyingInventory }] = useMutation(
    COPY_CHEMICAL_INVENTORY,
    {
      onCompleted({ copyFacilityChemicals }) {
        alerts.success(
          `Successfully copied chemical inventory from ${copyFacilityChemicals.fromYear}. Processing your chemical summary may take a few seconds.`
        );
      },
      onError(err) {
        const errorCode = err.graphQLErrors?.[0]?.extensions?.code;
        if (errorCode === "NO_INVENTORY_TO_COPY") {
          alerts.info("No chemical inventory to copy");
        } else {
          alerts.error("There was an error copying this facility's chemical inventory", err);
        }
      },
      refetchQueries: ["FacilityChemicals"],
      variables: {
        facilityId,
        toYear: reportingYear,
      },
    }
  );

  const { data: facilityInventoryStatus } = useQuery(FACILITY_INVENTORY_STATUS, {
    variables: { facilityId, toYear: reportingYear },
    fetchPolicy: "cache-and-network",
  });
  const handleDelete = useCallback(() => {
    if (deletingChemical) deleteFacilityChemical({ variables: { id: deletingChemical.id } });
  }, [deleteFacilityChemical, deletingChemical]);

  const handleAddEditChemical = useCallback(
    (facilityChemicalId?: string) => {
      if (!facilityChemicalId) {
        setAddingChemical(true);
        return;
      }
      if (facilityChemicalId) {
        pushIdToUrl(facilityChemicalId);
      }
    },
    [pushIdToUrl, setAddingChemical]
  );

  const columns = useMemo<Array<OmnisearchGridColDef<FacilityChemicalRow>>>(() => {
    const cols: Array<OmnisearchGridColDef<FacilityChemicalRow>> = [
      {
        headerName: "Chemical",
        field: "chemical.name",
        flex: 2,
        renderCell(params) {
          if (!params.row.chemical) return "";
          const isExcluded = !!params.row.chemical.noHazardsNotReporting;
          const hazardsPresent =
            chemicalExclusionWarning({
              noHazardsNotReporting: params.row.chemical.noHazardsNotReporting,
              physicalHazards: params.row.chemical.physicalHazards,
              healthHazards: params.row.chemical.healthHazards,
            }) === "hazards_present";
          return (
            <Box display="flex" alignItems="center" gap={1} minWidth={0} width="100%">
              <Box flex="1 1 auto" minWidth={0}>
                <TextCellWithTooltip tooltip={formatChemicalName(params.row.chemical)}>
                  {formatChemicalName(params.row.chemical, {
                    showCommonName: true,
                    showCasNumber: false,
                    showPureOrMixture: false,
                    showMixtureDetails: false,
                    showStateOfMatter: true,
                    showAlternateId: false,
                  })}
                </TextCellWithTooltip>
              </Box>
              {isExcluded && (
                <Tooltip title="Excluded from Tier II reporting">
                  <Chip size="small" variant="outlined" color="warning" label="Excluded" />
                </Tooltip>
              )}
              {hazardsPresent && (
                <Tooltip title={CHEMICAL_EXCLUSION_HAZARDS_TOOLTIP}>
                  <WarningAmber color="warning" fontSize="small" />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        headerName: "CAS #",
        field: "chemical.casNumber",
        flex: 1,
        renderCell: ({ row }) => (
          <TextCellWithTooltip>{row.chemical?.casNumber}</TextCellWithTooltip>
        ),
      },
      {
        headerName: "Alt ID",
        field: "chemical.alternateId",
        flex: 0.9,
        renderCell: ({ row }) => (
          <TextCellWithTooltip>{row.chemical?.alternateId}</TextCellWithTooltip>
        ),
      },
      {
        headerName: "Is EHS",
        field: "chemical.isEhs",
        flex: 0.6,
        headerAlign: "center",
        align: "center",
        filterKeyType: "boolean",
        renderCell(params) {
          const isEhs = params.row.chemical?.isEhs;
          if (isEhs) return <Check />;
          return <></>;
        },
      },
      {
        headerName: "Max Amt",
        field: "maxAmount",
        flex: 1,
        filterKeyType: "number",
        valueGetter(_, row) {
          if (row.maxAmount) {
            return prettyPrintQuantity({
              amount: row.maxAmount,
              unit: row.unit,
              state: facilityQuery?.facility?.state,
            });
          } else if (row.maxAmountCode) {
            return prettyPrintAmountCode(
              row.facility?.state ?? "",
              row.maxAmountCode,
              row.unit,
              true
            );
          }
          return "";
        },
        renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
      },
      {
        headerName: "Avg Amt",
        field: "averageAmount",
        flex: 1,
        filterKeyType: "number",
        valueGetter(_, row) {
          if (row.averageAmount) {
            return prettyPrintQuantity({
              amount: row.averageAmount,
              unit: row.unit,
              state: facilityQuery?.facility?.state,
            });
          } else if (row.averageAmountCode) {
            return prettyPrintAmountCode(
              row.facility?.state ?? "",
              row.averageAmountCode,
              row.unit,
              true
            );
          }
          return "";
        },
        renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
      },
    ];

    if (!isOtherChemicals) {
      cols.push(
        {
          headerName: "Over Threshold",
          field: "isOverThreshold",
          headerAlign: "center",
          align: "center",
          flex: 0.6,
          filterKeyType: "boolean",
          renderCell(params) {
            return params.row.reportingFacilityChemical?.isOverThreshold ? <Check /> : <></>;
          },
        },
        {
          field: "willReport",
          headerName: "Will Report",
          headerAlign: "center",
          align: "center",
          flex: 0.6,
          filterKeyType: "boolean",
          renderCell({ row }) {
            const willReport = isChemicalReporting({
              isAlwaysReported: row.isAlwaysReported,
              isExempt: row.isExempt,
              isOverThreshold: row.reportingFacilityChemical?.isOverThreshold,
              chemical: {
                noHazardsNotReporting: row.chemical?.noHazardsNotReporting,
              },
            });

            return willReport ? <Check /> : <></>;
          },
        }
      );
    }
    cols.push(
      {
        field: "issues",
        headerName: "Issues",
        align: "center",
        headerAlign: "center",
        flex: 0.6,
        renderCell: ({ row: { issues } }) => {
          const shouldIgnore = ignoredIssues.some((issue) =>
            issues.some((i) => i.modelId === issue.modelId)
          );
          return <IssueCount issueCount={issues.length} ignoreForReport={shouldIgnore} />;
        },
        sortable: false,
      },
      {
        field: "actions",
        type: "actions",
        flex: 0.8,
        getActions: ({ row }) => {
          return [
            <Tooltip title="Edit Chemical" key="editChemical">
              <GridActionsCellItem
                onClick={() => {
                  handleAddEditChemical(row.id);
                }}
                label="Edit Chemical"
                icon={<Edit />}
                key="edit"
              />
            </Tooltip>,
            <Tooltip title="Remove Chemical" key="removeChemical">
              <GridActionsCellItem
                onClick={() => setDeletingChemical(row)}
                label="Delete Chemical"
                key="delete"
                icon={<Delete />}
              />
            </Tooltip>,
          ];
        },
      }
    );

    return cols;
  }, [handleAddEditChemical, isOtherChemicals, ignoredIssues, facilityQuery?.facility?.state]);

  const defaultSearch = useMemo(
    () => `facilityId:${facilityId} reportingYear:${reportingYear}`,
    [facilityId, reportingYear]
  );

  const commandButtons = useMemo(() => {
    const buttons: ReactNode[] = [];
    if (!reportId) {
      buttons.push(
        <TierIIYearPicker
          key="yearPicker"
          selectProps={{
            size: "small",
          }}
          persistToLocalStorage
        />
      );
    }
    buttons.push(
      <CommandButton variant="contained" key="addChemical" onClick={() => handleAddEditChemical()}>
        Add Chemical
      </CommandButton>
    );
    return buttons;
  }, [reportId, handleAddEditChemical]);

  return (
    <>
      <OmnisearchDataGrid
        persistenceKey="facility-chemicals"
        isURLDriven
        columns={columns}
        dataQuery={FACILITY_CHEMICALS_QUERY}
        defaultSearch={defaultSearch}
        initialSortModel={[{ field: "chemical.name", sort: "asc" }]}
        getCount={(data) => data.facilityChemicals.count}
        getItems={(data) => data.facilityChemicals.items}
        withPadding={false}
        commandButtons={commandButtons}
        excludeFilterColumns={["issues"]}
        additionalFilterColumns={[
          {
            key: "chemical.noHazardsNotReporting",
            header: "Excluded from Tier II",
            filterKeyType: "boolean",
          },
        ]}
        noDataMessage={`No chemical information has been added to this facility for ${reportingYear}${
          !facilityInventoryStatus?.facilityInventoryStatus.exists ? " or any previous year" : ""
        }.`}
        noDataButtonText={
          facilityInventoryStatus?.facilityInventoryStatus.exists
            ? `Copy Inventory from ${facilityInventoryStatus?.facilityInventoryStatus.mostRecentYear}`
            : undefined
        }
        noDataOnButtonClick={copyChemicalInventory}
        onRowClick={(params) => handleAddEditChemical(params.row.id)}
        otherLoading={isCopyingInventory}
      />
      <ConfirmDialog
        open={!!deletingChemical}
        onClose={() => setDeletingChemical(undefined)}
        onConfirm={handleDelete}
        header={`Are you sure you want to delete this chemical from the facility's inventory?`}
        resources={[
          {
            resourceType: "Chemical Inventory",
            resourceName: deletingChemical?.chemical?.name,
          },
          {
            resourceType: "Reporting Year",
            resourceName: reportingYear,
          },
        ]}
        info={
          <DeleteChemicalMessage
            chemicalName={deletingChemical?.chemical?.name}
            forMeasurements={false}
          />
        }
        loading={isDeleting}
      />
      {(facilityChemicalId || addingChemical) && (
        <AddEditFacilityChemicalDialog
          facilityChemicalId={facilityChemicalId}
          reportingYear={reportingYear}
          facilityId={facilityId}
          reportId={reportId}
          onClose={() => {
            if (addingChemical) {
              setAddingChemical(false);
            } else {
              popIdFromUrl();
            }
          }}
        />
      )}
    </>
  );
}

export function DeleteChemicalMessage({
  chemicalName,
  forMeasurements,
}: {
  chemicalName?: string;
  forMeasurements?: boolean;
}) {
  const { hasProducts } = useProducts();

  return (
    <>
      <Typography component="span">
        This action will:
        <ul>
          <li>Remove {chemicalName} from your chemical inventory</li>
          {forMeasurements && <li>Remove all daily measurements for this chemical</li>}
        </ul>
      </Typography>
      <Typography component="span">
        This action will <b>not:</b>
        <ul>
          {hasProducts && (
            <li>Remove {chemicalName} contained in products from your product inventory</li>
          )}
          <li>Change {chemicalName} in your chemical catalog</li>
          <li>Affect previous reporting years</li>
        </ul>
      </Typography>
    </>
  );
}
